import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { NewsManagerService } from 'src/app/services/news-manager/news-manager.service';
import { NewsManagerModalService } from 'src/app/services/news-manager-modal/news-manager-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-news-manager',
  templateUrl: './news-manager.component.html',
  styleUrls: ['./news-manager.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(-100%)', opacity: 0 }),
        animate('300ms ease-in', style({ transform: 'translateY(0%)', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ transform: 'translateY(-100%)', opacity: 0 }))
      ])
    ])
  ]
})
export class NewsManagerComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  newsItems: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedNewsItems: string[] = [];

  constructor(
    private _newsManagerService: NewsManagerService,
    private _newsManagerModalService: NewsManagerModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadNewsItems();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  private loadNewsItems(page: number = 1, search: string = '', filters: any = {}): void {
    this.isLoading = true;

    this.subscriptions.push(
      this._newsManagerService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.newsItems = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.newsItems = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading news items:', error);
          this.newsItems = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadNewsItems(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadNewsItems(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadNewsItems();
  }

  openAddNewsItemModal(): void {
    this._newsManagerModalService.clearSelectedNewsManager();
    const modalElement = document.getElementById('kt_modal_news_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(newsItem: any): void {
    this._newsManagerModalService.setSelectedNewsManager(newsItem);
    const modalElement = document.getElementById('kt_modal_news_manager_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteNewsItem(newsItem: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${newsItem.news_title}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(newsItem);
      }
    });
  }

  private performDelete(newsItem: any): void {
    this.subscriptions.push(
      this._newsManagerService.DeleteDataById(newsItem._id).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'News item has been deleted successfully.',
              icon: 'success',
              timer: 2000,
              showConfirmButton: false
            });
            this.loadNewsItems(this.currentPage, this.searchTerm, this.filters);
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete news item.',
              icon: 'error'
            });
          }
        },
        error: (error: any) => {
          console.error('Delete error:', error);
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting the news item.',
            icon: 'error'
          });
        }
      })
    );
  }

  // Bulk operations
  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedNewsItems = this.newsItems.map(item => item._id);
    } else {
      this.selectedNewsItems = [];
    }
    this.toggleToolbars();
  }

  toggleSelection(newsItemId: string, event: any): void {
    if (event.target.checked) {
      this.selectedNewsItems.push(newsItemId);
    } else {
      this.selectedNewsItems = this.selectedNewsItems.filter(id => id !== newsItemId);
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    // This method can be used to show/hide bulk action toolbars
    // Implementation depends on your UI requirements
  }

  // Event handlers for modal events
  onNewsItemAdded(): void {
    console.log('News item added, refreshing list...');
    this.loadNewsItems(this.currentPage, this.searchTerm, this.filters);
  }

  onNewsItemUpdated(): void {
    console.log('News item updated, refreshing list...');
    this.loadNewsItems(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(newsItemId: string): boolean {
    return this.selectedNewsItems.includes(newsItemId);
  }

  isAllSelected(): boolean {
    return this.newsItems.length > 0 && this.selectedNewsItems.length === this.newsItems.length;
  }

  isIndeterminate(): boolean {
    return this.selectedNewsItems.length > 0 && this.selectedNewsItems.length < this.newsItems.length;
  }

  // Format date for display
  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  }

  // Get status display
  getStatusDisplay(show: boolean): string {
    return show ? 'Yes' : 'No';
  }
}
